/**
 * usePickSlips Hook
 * Manages pick slip state and operations
 */

import { useState, useMemo, useCallback } from 'react';
import { Platform } from 'react-native';
import { useDialog } from '../../shared/hooks/useDialog';
import { logger } from '../../helpers/logger.helper';
import { MAX_GAMES_PER_CHALLENGE } from '../constants/create.constants';
import { isInSameExclusiveFamily, isMarketInExclusiveFamily } from '../constants/exclusiveMarkets.constants';
import { replaceSlipPreservingOrder } from '../helpers/replaceSlipPreservingOrder.helper';
import { isPremiumMarketIdHiddenOnIos } from '../helpers/premiumMarketsIosDefer.helper';
import type { PickSlip, Game } from '../types/create.types';
import { isMatchWinnerSlip } from '../types/create.types';
import { getSportDefinitionOrDefault } from '../../sports/sports.constants';

interface UsePickSlipsReturn {
  pickSlips: PickSlip[];
  totalPoints: number;
  selectedGamesByLeague: Record<string, Array<{ game: Game; slip: PickSlip }>>;
  handlePickSelect: (gameId: string, pick: 'home' | 'draw' | 'away' | null) => void;
  handleUpdatePick: (gameId: string, prediction: 'home' | 'draw' | 'away') => void;
  handleRemovePick: (gameId: string) => void;
  handleRemoveSlip: (slip: PickSlip) => void;
  handleDeleteMarkets: (gameId: string, marketIds: number[]) => void;
  getPickSlipForGame: (gameId: string) => PickSlip | undefined;
  setPickSlips: React.Dispatch<React.SetStateAction<PickSlip[]>>;
  handleAddOtherPick: (gameId: string, marketId: number, marketName: string, value: string, odd: number, homeTeam?: string, awayTeam?: string) => void;
  isOtherPickSelected: (gameId: string, marketId: number, value: string) => boolean;
}

export const usePickSlips = (games: Game[], initialPickSlips?: PickSlip[], maxGames: number = MAX_GAMES_PER_CHALLENGE): UsePickSlipsReturn => {
  const { showAlert } = useDialog();
  const [pickSlips, setPickSlips] = useState<PickSlip[]>(initialPickSlips ?? []);

  // O(1) game lookup – avoid O(slips × games) in totalPoints and selectedGamesByLeague (was 10x+ slower with 1000 games)
  const gameById = useMemo(() => new Map(games.map((g) => [g.id, g])), [games]);

  /**
   * The market a one-tap pick on THIS game means.
   *
   * A quick-pick slip carries `marketId: null` and is stamped with the sport's `primaryMarketId`
   * at submission (`buildPicksForGame`). Everything below therefore has to ask the same question
   * the same way, per game — it used to compare against football's MATCH_WINNER_MARKET_ID for
   * every sport, which broke basketball in three places at once: the market-106 tiles rendered
   * unselected because the quick pick was invisible to `isOtherPickSelected`; tapping one added a
   * SECOND slip for the same game instead of replacing the first; and `handleDeleteMarkets` could
   * not clear a basketball quick pick at all. Two contradictory picks then double-counted
   * `totalPoints`, ate two `maxGames` slots, and collided on the server's
   * `challengeId_userId_matchId_marketId` upsert, which silently discarded the visible one.
   *
   * Football is unaffected: its `primaryMarketId` IS 1.
   */
  const primaryMarketIdForGame = useCallback(
    (gameId: string) => getSportDefinitionOrDefault(gameById.get(gameId)?.sport).primaryMarketId,
    [gameById]
  );

  // Calculate total points: match-winner uses game odds, other slips use slip.odd
  const totalPoints = useMemo(() => {
    return pickSlips.reduce((sum, slip) => {
      if (slip.marketId != null && slip.odd != null) {
        return sum + slip.odd;
      }
      const game = gameById.get(slip.gameId);
      if (!game) return sum;
      let odds = 0;
      switch (slip.prediction) {
        case 'home':
          odds = game.odds.home ?? 0;
          break;
        case 'away':
          odds = game.odds.away ?? 0;
          break;
        case 'draw':
          odds = game.odds.draw || 0;
          break;
      }
      return sum + odds;
    }, 0);
  }, [pickSlips, gameById]);

  // Group selected games by league
  const selectedGamesByLeague = useMemo(() => {
    return pickSlips.reduce((acc, slip) => {
      const game = gameById.get(slip.gameId);
      if (!game) {
        return acc;
      }

      const leagueKey = game.league || 'Other';
      if (!acc[leagueKey]) {
        acc[leagueKey] = [];
      }

      acc[leagueKey].push({ game, slip });
      return acc;
    }, {} as Record<string, Array<{ game: Game; slip: PickSlip }>>);
  }, [pickSlips, gameById]);

  const handlePickSelect = useCallback((gameId: string, pick: 'home' | 'draw' | 'away' | null) => {
    if (pick === null) {
      setPickSlips(prev => prev.filter(slip => slip.gameId !== gameId || slip.marketId != null));
      return;
    }
    setPickSlips(prev => {
      const existingMatch = prev.find(slip => slip.gameId === gameId && isMatchWinnerSlip(slip));
      if (existingMatch) {
        return prev.map(slip =>
          slip.gameId === gameId && isMatchWinnerSlip(slip)
            ? { ...slip, prediction: pick, confidence: 2 }
            : slip
        );
      }
      if (prev.length >= maxGames) {
        const upgradeHint = maxGames === MAX_GAMES_PER_CHALLENGE ? ' Upgrade to Pro for up to 15.' : '';
        void showAlert({
          title: 'Pick Limit Reached',
          message: `You can only add up to ${maxGames} picks.${upgradeHint} Please remove a pick first if you want to add a different one.`,
        });
        return prev;
      }
      return [...prev, { gameId, prediction: pick, confidence: 2 }];
    });
  }, [showAlert, maxGames]);

  const handleAddOtherPick = useCallback(
    (gameId: string, marketId: number, marketName: string, value: string, odd: number, homeTeam?: string, awayTeam?: string) => {
      if (Platform.OS === 'ios' && isPremiumMarketIdHiddenOnIos(marketId)) return;

      // A FIELD sport's primary market (Race Winner) also lands here, but its value is a driver
      // name rather than a side, so `prediction` stays null and it falls through to the generic
      // market branch below — which is right: a race has no one-tap home/away pick.
      if (marketId === primaryMarketIdForGame(gameId)) {
        const prediction =
          value === 'Home' ? 'home' : value === 'Draw' ? 'draw' : value === 'Away' ? 'away' : null;
        if (typeof __DEV__ === 'boolean' && __DEV__) {
          logger.debug('[usePickSlips] Match Winner (1X2) from match details', { gameId, value, prediction });
        }
        if (prediction) {
          setPickSlips(prev => {
            const existing = prev.find(s => s.gameId === gameId && isMatchWinnerSlip(s));
            if (existing && existing.prediction === prediction) {
              return prev.filter(s => !(s.gameId === gameId && isMatchWinnerSlip(s)));
            }
            const shouldRemove = (s: PickSlip) => s.gameId === gameId && isMatchWinnerSlip(s);
            const without = prev.filter(s => !shouldRemove(s));
            if (without.length >= maxGames && !existing) {
              const upgradeHint = maxGames === MAX_GAMES_PER_CHALLENGE ? ' Upgrade to Pro for up to 15.' : '';
              void showAlert({
                title: 'Pick Limit Reached',
                message: `You can only add up to ${maxGames} picks.${upgradeHint} Please remove a pick first if you want to add a different one.`,
              });
              return prev;
            }
            return replaceSlipPreservingOrder(prev, shouldRemove, { gameId, prediction, confidence: 2 });
          });
          return;
        }
      }

      setPickSlips(prev => {
        const existingOther = prev.find(
          slip => slip.gameId === gameId && slip.marketId === marketId
        );
        if (existingOther && existingOther.value === value) {
          return prev.filter(
            slip => !(slip.gameId === gameId && slip.marketId === marketId)
          );
        }
        const inExclusiveFamily = isMarketInExclusiveFamily(marketId);
        const shouldRemove = (slip: PickSlip) =>
          inExclusiveFamily
            ? slip.gameId === gameId &&
              slip.marketId != null &&
              isInSameExclusiveFamily(slip.marketId, marketId)
            : slip.gameId === gameId && slip.marketId === marketId;
        const withoutReplaced = prev.filter((slip) => !shouldRemove(slip));
        if (withoutReplaced.length >= maxGames && !existingOther) {
          const upgradeHint = maxGames === MAX_GAMES_PER_CHALLENGE ? ' Upgrade to Pro for up to 15.' : '';
          void showAlert({
            title: 'Pick Limit Reached',
            message: `You can only add up to ${maxGames} picks.${upgradeHint} Please remove a pick first if you want to add a different one.`,
          });
          return prev;
        }
        const newSlip = { gameId, marketId, marketName, value, odd, ...(homeTeam != null && { homeTeam }), ...(awayTeam != null && { awayTeam }) };
        return replaceSlipPreservingOrder(prev, shouldRemove, newSlip);
      });
    },
    [showAlert, maxGames, primaryMarketIdForGame]
  );

  const isOtherPickSelected = useCallback(
    (gameId: string, marketId: number, value: string) => {
      const pred = value === 'Home' ? 'home' : value === 'Draw' ? 'draw' : value === 'Away' ? 'away' : null;
      // A FIELD sport's primary market (Race Winner) has driver-name values, so `pred` is null and it is
      // stored as a generic market slip by `handleAddOtherPick`; check it the same way below.
      if (marketId === primaryMarketIdForGame(gameId) && pred != null) {
        return pickSlips.some(s => s.gameId === gameId && isMatchWinnerSlip(s) && s.prediction === pred);
      }
      return pickSlips.some(
        slip => slip.gameId === gameId && slip.marketId === marketId && slip.value === value
      );
    },
    [pickSlips, primaryMarketIdForGame]
  );

  const handleUpdatePick = useCallback((gameId: string, prediction: 'home' | 'draw' | 'away') => {
    setPickSlips(prev =>
      prev.map(slip => {
        if (slip.gameId === gameId && isMatchWinnerSlip(slip)) {
          return { ...slip, prediction };
        }
        return slip;
      })
    );
  }, []);

  const handleRemovePick = useCallback((gameId: string) => {
    setPickSlips(prev => prev.filter(slip => slip.gameId !== gameId));
  }, []);

  const handleRemoveSlip = useCallback((slipToRemove: PickSlip) => {
    setPickSlips(prev =>
      prev.filter(
        s =>
          !(
            s.gameId === slipToRemove.gameId &&
            (slipToRemove.marketId != null
              ? s.marketId === slipToRemove.marketId && s.value === slipToRemove.value
              : isMatchWinnerSlip(s))
          )
      )
    );
  }, []);

  /** Bulk delete: remove all slips for the given game whose marketId is in marketIds, plus match-winner slip if 1 is in marketIds. Single state update. */
  const handleDeleteMarkets = useCallback((gameId: string, marketIds: number[]) => {
    if (marketIds.length === 0) return;
    const marketIdSet = new Set(marketIds);
    const removeMatchWinner = marketIdSet.has(primaryMarketIdForGame(gameId));
    setPickSlips(prev =>
      prev.filter((s) => {
        if (s.gameId !== gameId) return true;
        if (isMatchWinnerSlip(s)) return !removeMatchWinner;
        return s.marketId == null || !marketIdSet.has(s.marketId);
      })
    );
  }, [primaryMarketIdForGame]);

  const getPickSlipForGame = useCallback(
    (gameId: string) =>
      pickSlips.find(slip => slip.gameId === gameId && isMatchWinnerSlip(slip)),
    [pickSlips]
  );

  return {
    pickSlips,
    totalPoints,
    selectedGamesByLeague,
    handlePickSelect,
    handleUpdatePick,
    handleRemovePick,
    handleRemoveSlip,
    handleDeleteMarkets,
    getPickSlipForGame,
    setPickSlips,
    handleAddOtherPick,
    isOtherPickSelected,
  };
};

